import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box, Tabs, Tab, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography,
  styled, Grid
} from '@mui/material';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { GlobalStateContext } from '../../context/empcontext';
import { AuthContext } from '../../context/logeduser';
import {
  useGetSingleEmployeeDetails, useUpdateEmployee,
  useUpdateEmployeeLeaves, useUploadDocument,
  useGetEmployeeAssets, useAddEmployeeAsset, useDeleteEmployeeAsset,
  useUpdatePhoto, useGetEmployeePublishedPayslips
} from '../../hooks/useEmployees';
import {
  GET_REPORTING_MANAGER_OPTIONS, GET_BRANCHES, GET_LOCATIONS, GET_TEAM_HEADS,
  GET_DEPARTMENTS, GET_DESIGNATIONS,
} from '../../api';
import ProfileHeader from './PortalComponents/ProfileHeader';
import PersonalTab from './PortalComponents/PersonalTab';
import JobTab from './PortalComponents/JobTab';
import DocumentsTab from './PortalComponents/DocumentsTab';
import AssetsTab from './PortalComponents/AssetTab';
import PayslipsTab from './PortalComponents/PayslipsTab';
import ProfileTimelineTab from './PortalComponents/ProfileTimelineTab';
import SaveIcon from '@mui/icons-material/Save';
import {
  getLeaveCaps,
  isProbation,
  validateLeaveField,
  validateLeaveForm,
} from '../../utils/leaveCaps';
import {
  firstIdentifierError,
  normalizeIndianCode,
  validateEmployeeIdentifiers,
} from './employeeFormValidation';
import {
  createEmployeeEditDraft,
  hasRestorableEmployeeEditDraft,
  mergeEmployeeEditDraft,
  reconcileOrganizationSelection,
} from './organizationSelection';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CustomFieldsSection, {
  useCustomFieldDefinitions,
  validateCustomFieldValues,
} from './CustomFieldsSection';

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };

const TabPanel = styled(Box)(({ theme }) => ({
  paddingTop: '24px',
  paddingBottom: '100px',
  animation: 'fadeIn 0.4s ease-in-out',
  '@keyframes fadeIn': {
    '0%': { opacity: 0, transform: 'translateY(10px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
}));

const ActionFab = styled(Button)(({ theme }) => ({
  position: 'fixed',
  bottom: '30px',
  left: '50%',
  transform: 'translateX(-50%)',
  borderRadius: '30px',
  padding: '12px 40px',
  textTransform: 'none',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  color: '#fff',
  fontWeight: '700',
  boxShadow: '0 10px 24px rgba(47, 107, 255, 0.35)',
  '&:hover': {
    background: 'linear-gradient(135deg,#2f6bff,#2557d6)',
    transform: 'translateX(-50%) scale(1.05)',
  },
  transition: 'all 0.3s ease',
  zIndex: 1000,
}));

const Salary = () => {
  const { empid } = useParams();
  const navigate = useNavigate();
  const employeeId = empid ? empid.replace(':', '') : '';
  const { setFormData: setGlobalFormData } = useContext(GlobalStateContext);
  const { authData } = useContext(AuthContext);
  // Salary/payslip data stays behind payroll permission, even though this is the employee page.
  // Unrestricted admins (is_restricted=false) always pass.
  const canViewPayroll =
    !authData?.admin?.is_restricted || authData?.admin?.permissions?.payroll?.view === true;
  const timelineTabIndex = canViewPayroll ? 5 : 4;
  const [activeTab, setActiveTab] = useState(0);
  const [, setIsEditMode] = useState(false);
  const customFields = useCustomFieldDefinitions();
  const serverBaselineRef = useRef(null);
  
  useEffect(() => {
    setIsInitialized(false);
    serverBaselineRef.current = null;
  }, [employeeId]);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [leaveBalances, setLeaveBalances] = useState({ total_leaves: '', sick_leaves: '', casual_leaves: '' });
  const [leaveErrors, setLeaveErrors] = useState({});

  const handleLeaveChange = (event) => {
    const { name, value } = event.target;
    const status = formData.employment_status;
    const error = validateLeaveField(name, value, status);
    if (error && !leaveErrors[name]) {
      toast.error(error, { autoClose: 4000, position: 'top-right' });
    }
    setLeaveBalances((prev) => ({ ...prev, [name]: value }));
    setLeaveErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleLeaveSubmit = () => {
    const errors = validateLeaveForm({
      employmentStatus: formData.employment_status,
      ...leaveBalances,
    });
    setLeaveErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the highlighted fields before saving.', {
        autoClose: 4000, position: 'top-right',
      });
      return;
    }

    updateLeaves(
      {
        employeeNo: formData.employee_no,
        leaveData: {
          total_leaves: leaveBalances.total_leaves !== '' ? parseFloat(leaveBalances.total_leaves) : undefined,
          sick_leaves: leaveBalances.sick_leaves !== '' ? parseFloat(leaveBalances.sick_leaves) : undefined,
          casual_leaves: leaveBalances.casual_leaves !== '' ? parseFloat(leaveBalances.casual_leaves) : undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success('Leave balance updated successfully.', {
            autoClose: 4000, position: 'top-right',
          });
          setLeaveModalOpen(false);
          setLeaveErrors({});
        },
        onError: (error) => {
          const backendMsg = error?.response?.data?.error
            || error?.response?.data?.detail
            || 'Failed to update leave balance. Please try again.';
          toast.error(backendMsg, { autoClose: 4000, position: 'top-right' });
        },
      }
    );
  };
  const [masterData, setMasterData] = useState({
    managers: [], branches: [], locations: [], teamHeads: [], departments: [], designations: [],
  });
  const [loadingMaster, setLoadingMaster] = useState(false);
  const [masterDataError, setMasterDataError] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Local state for the form data
  const [formData, setFormData] = useState({
    employee_no: '',
    name: '',
    status: '',
    designation: '',
    designation_id: null,
    date_of_birth: '',
    gender: '',
    location: '',
    nationality: '',
    mobile_number: '',
    email: '',
    date_of_joining: '',
    department: '',
    department_id: null,
    reporting_manager: '',
    reporting_manager_id: null,
    confirmation_date: '',
    employment_status: '',
    pan_no: '',
    uan_no: '',
    bank_account_no: '',
    ifsc_code: '',
    branch_name: '',
    branch_id: null,
    aadhaar_number: '',
    division: '',
    attendance_schema: '',
    level_cost_center: '',
    grade: '',
    team_head: '',
    ESIno: '',
    linkedin: '',
    facebook: '',
    instagram: '',
    twitter: '',
    form_url: '',
    custom_attributes: {},
  });

  // Hooks
  const { data: empdata, isLoading: dataLoading, error: dataError, refetch } = useGetSingleEmployeeDetails(employeeId);
  const { mutate: updateEmployee, isPending: updating } = useUpdateEmployee();
  const { mutate: updateLeaves } = useUpdateEmployeeLeaves();
  const { mutate: uploadDoc } = useUploadDocument();
  const { mutate: updatePhoto } = useUpdatePhoto();

  // Asset Hooks
  const { data: assets = [] } = useGetEmployeeAssets(employeeId);
  const { mutate: addAsset } = useAddEmployeeAsset();
  const { mutate: deleteAsset } = useDeleteEmployeeAsset();

  // Structured per-month payslips (PUBLISHED only) for the Payslips tab
  const { data: payslipPayload } = useGetEmployeePublishedPayslips(employeeId);

  useEffect(() => {
    if (empdata && !isInitialized) {
      console.log("Salary.js - Initializing Data:", empdata);
      const employee = empdata.employee || empdata;
      if (employee) {
        const cleanData = {};
        Object.keys(formData).forEach(key => {
          cleanData[key] = employee[key] ?? employee.joining_info?.[key] ?? '';
        });
        cleanData.department_id = employee.department_id ?? employee.department_ref ?? cleanData.department_id ?? null;
        cleanData.designation_id = employee.designation_id ?? employee.designation_ref ?? cleanData.designation_id ?? null;
        cleanData.branch_id = employee.branch_id ?? employee.branch_ref ?? cleanData.branch_id ?? null;
        cleanData.reporting_manager_id = employee.reporting_manager_id ?? employee.reporting_manager_fk ?? cleanData.reporting_manager_id ?? null;
        serverBaselineRef.current = cleanData;

        // Try to restore from localStorage first
        const savedDraft = localStorage.getItem(`emp_edit_draft_${employeeId}`);
        if (savedDraft) {
          try {
            const parsedDraft = JSON.parse(savedDraft);
            const restoredData = mergeEmployeeEditDraft(cleanData, parsedDraft);
            setFormData(restoredData);
            setGlobalFormData(prev => ({ ...prev, ...restoredData }));
            if (hasRestorableEmployeeEditDraft(cleanData, parsedDraft)) {
              toast.info("Unsaved changes restored from draft", { autoClose: 2000 });
            }
          } catch (e) {
            console.error("Error parsing draft:", e);
            setFormData(cleanData);
            setGlobalFormData(prev => ({ ...prev, ...cleanData }));
          }
        } else {
          setFormData(cleanData);
          setGlobalFormData(prev => ({ ...prev, ...cleanData }));
        }
        setIsInitialized(true);
      }
    }
  }, [empdata, isInitialized, employeeId, setGlobalFormData, formData]);

  // Legacy employees can have Department/Designation names but no relational
  // IDs. Once this tenant's active master data is available, safely resolve only
  // unique name matches. Ambiguous or missing values deliberately remain
  // unassigned for HR to select rather than guessing an organization mapping.
  useEffect(() => {
    if (!isInitialized || loadingMaster || masterData.departments.length === 0) return;
    setFormData((previous) => reconcileOrganizationSelection(
      previous,
      masterData.departments,
      masterData.designations,
    ));
  }, [isInitialized, loadingMaster, masterData.departments, masterData.designations]);

  // Effect to persist changes to localStorage
  useEffect(() => {
    if (isInitialized && serverBaselineRef.current) {
      const draft = createEmployeeEditDraft(serverBaselineRef.current, formData);
      localStorage.setItem(`emp_edit_draft_${employeeId}`, JSON.stringify(draft));
    }
  }, [formData, isInitialized, employeeId]);

  useEffect(() => {
    const fetchMasterData = async () => {
      setLoadingMaster(true);
      try {
        const [mgrs, brs, locs, ths, depts, desigs] = await Promise.all([
          GET_REPORTING_MANAGER_OPTIONS(),
          GET_BRANCHES(),
          GET_LOCATIONS(),
          GET_TEAM_HEADS(),
          GET_DEPARTMENTS(),
          GET_DESIGNATIONS(),
        ]);
        setMasterData({
          managers: mgrs || [],
          branches: brs || [],
          locations: locs || [],
          teamHeads: ths || [],
          departments: (depts || []).filter((item) => item.is_active),
          designations: (desigs || []).filter((item) => item.is_active),
        });
        setMasterDataError('');
      } catch (error) {
        console.error('Error fetching master data:', error);
        setMasterDataError('Organization and reporting-manager options could not be loaded. Please retry.');
      } finally {
        setLoadingMaster(false);
      }
    };
    fetchMasterData();
  }, []);

  if (dataLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh', gap: 2 }}>
        <CircularProgress sx={{ color: ACCENT }} />
        <Typography variant="body2" color="text.secondary">Fetching employee profile...</Typography>
      </Box>
    );
  }

  if (dataError || !empdata) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 6 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: '#fef2f2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ReceiptLongIcon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
            {dataError ? `Error: ${dataError.message}` : 'Employee profile not found'}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={() => navigate('/employees')}
            sx={{ ...GRADIENT_BTN, mt: 1, px: 3 }}
          >
            Back to Employee List
          </Button>
        </Box>
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const normalizedValue = ['pan_no', 'ifsc_code'].includes(name)
        ? normalizeIndianCode(value)
        : value;
      return { ...prev, [name]: normalizedValue };
    });
  };

  const handleDateChange = (name, value) => {
    // Format to YYYY-MM-DD for backend, use null (not '') for empty dates
    const formatted = value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : null;
    setFormData(prev => ({ ...prev, [name]: formatted }));
  };

  const handleSave = () => {
    // 1. Mandatory Field Validation
    const mandatoryFields = {
        'status': 'Status',
        'gender': 'Gender',
        'location': 'Region / Location',
        'branch_name': 'Branch Name',
        'employment_status': 'Employment Status',
        'attendance_schema': 'Attendance Schema',
        'team_head': 'Team Head'
    };

    const emptyFields = Object.entries(mandatoryFields)
        .filter(([key]) => !formData[key] || formData[key] === '')
        .map(([, label]) => label);

    if (emptyFields.length > 0) {
        toast.error(`Please fill mandatory fields: ${emptyFields.join(', ')}`);
        return;
    }

    const identifierError = firstIdentifierError(formData);
    if (identifierError) {
      toast.error(identifierError);
      return;
    }

    const customErrors = validateCustomFieldValues(
      customFields.definitions,
      formData.custom_attributes,
    );
    if (Object.keys(customErrors).length > 0) {
      const firstDefinition = customFields.definitions.find(
        (item) => customErrors[item.field_name]
      );
      toast.error(`${firstDefinition?.field_label || 'Custom field'}: ${Object.values(customErrors)[0]}`);
      return;
    }

    // 2. Final check for date formats before sending
    // Empty strings must become null — backend rejects empty strings for date fields
    const payload = { ...formData };
    ['date_of_birth', 'date_of_joining', 'confirmation_date'].forEach(field => {
        if (payload[field] && dayjs(payload[field]).isValid()) {
            payload[field] = dayjs(payload[field]).format('YYYY-MM-DD');
        } else {
            payload[field] = null;
        }
    });

    updateEmployee({
      employeeNo: formData.employee_no,
      formData: payload
    }, {
      onSuccess: () => {
        localStorage.removeItem(`emp_edit_draft_${employeeId}`);
        setIsEditMode(false);
        setIsInitialized(false); // Reset initialization so it can refetch fresh data
        refetch(); // Refresh data after save
      }
    });
  };

  const handlePhotoUpload = (file) => {
    if (file) {
      updatePhoto({ employee_no: formData.employee_no, file }, {
        onSuccess: () => {
          refetch(); // Explicitly refetch to update local state and ProfileHeader
        }
      });
    }
  };

  const handleDocumentUploadInitiated = (fileType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        uploadDoc({ employee_no: formData.employee_no, file_type: fileType, file });
      }
    };
    input.click();
  };

  const handleAddAsset = (assetData, file) => {
    addAsset({ employeeNo: empdata?.employee?.id || empdata?.id, assetData, file });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, position: 'relative' }}>
      <Box>
        {/* Top Navigation */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/employees')}
            variant="outlined"
            sx={OUTLINED_BTN}
          >
            Back to Employee List
          </Button>
        </Box>

        {/* Profile Info Header */}
        <ProfileHeader employee={empdata?.employee || empdata} onPhotoUpload={handlePhotoUpload}>
          <Button
            variant="outlined"
            onClick={() => {
              setLeaveBalances({
                total_leaves: empdata?.total_leaves || '',
                sick_leaves: empdata?.sick_leaves || '',
                casual_leaves: empdata?.casual_leaves || ''
              });
              setLeaveErrors({});
              setLeaveModalOpen(true);
            }}
            sx={{
              borderRadius: '12px',
              px: 4,
              py: 1.2,
              bgcolor: `${ACCENT}14`,
              color: ACCENT,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: 'none',
              border: `1px solid ${ACCENT}33`,
              '&:hover': {
                  bgcolor: `${ACCENT}22`,
                  borderColor: `${ACCENT}55`,
              },
              transition: 'all 0.2s ease'
            }}
          >
            Manage Leaves
          </Button>
        </ProfileHeader>

        {/* Tabs Menu */}
        <Box sx={{
            ...CARD_SX,
            mt: 4,
            mb: 2,
            position: 'sticky',
            top: 20,
            zIndex: 10,
            p: 0.75,
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.9rem',
                color: '#6b7280',
                minHeight: '48px',
                borderRadius: '14px',
                transition: 'all 0.2s',
                '&.Mui-selected': {
                    color: ACCENT,
                    bgcolor: `${ACCENT}14`,
                }
              },
              '& .MuiTabs-indicator': { display: 'none' }
            }}
          >
            <Tab label="Personal" />
            <Tab label="Bank & Job" />
            <Tab label="Documents" />
            <Tab label="Assets" />
            {canViewPayroll && <Tab label="Payslips" />}
            <Tab label="Timeline" />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        {activeTab === 0 && (
          <TabPanel>
            <PersonalTab 
                formData={formData} 
                handleInputChange={handleInputChange} 
                handleDateChange={handleDateChange}
                masterData={masterData}
            />
          </TabPanel>
        )}

        {activeTab === 1 && (
          <TabPanel>
            <JobTab 
                formData={formData} 
                handleInputChange={handleInputChange} 
                handleDateChange={handleDateChange}
                masterData={masterData}
                loadingMaster={loadingMaster}
                masterDataError={masterDataError}
                identifierErrors={validateEmployeeIdentifiers(formData)}
            />
            <Box sx={{ mt: 3 }}>
              <CustomFieldsSection
                definitions={customFields.definitions}
                loading={customFields.loading}
                error={customFields.error}
                values={formData.custom_attributes || {}}
                onChange={(customAttributes) => setFormData((current) => ({
                  ...current, custom_attributes: customAttributes,
                }))}
              />
            </Box>
          </TabPanel>
        )}

        {activeTab === 2 && (
          <TabPanel>
            <DocumentsTab
              documents={empdata?.documents?.[0] || {}}
              onUploadInitiated={handleDocumentUploadInitiated}
              formData={formData}
            />
          </TabPanel>
        )}

        {activeTab === 3 && (
          <TabPanel>
            <AssetsTab
              assets={assets}
              onAddAsset={handleAddAsset}
              onDeleteAsset={(id) => deleteAsset(id)}
            />
          </TabPanel>
        )}

        {activeTab === 4 && canViewPayroll && (
          <TabPanel>
            <PayslipsTab payslips={payslipPayload?.payslips || []} employeeId={employeeId} />
          </TabPanel>
        )}

        {activeTab === timelineTabIndex && (
          <TabPanel>
            <ProfileTimelineTab employeeId={employeeId} />
          </TabPanel>
        )}

        {/* Floating Action Button for Save */}
        {activeTab !== timelineTabIndex && (
          <ActionFab
            variant="contained"
            onClick={handleSave}
            startIcon={<SaveIcon />}
          >
            {updating ? 'Saving...' : 'Save Changes'}
          </ActionFab>
        )}


        {/* Leave Modal */}
        <Dialog open={leaveModalOpen} onClose={() => setLeaveModalOpen(false)} PaperProps={{ sx: { borderRadius: '22px', p: 1 } }}>
          <DialogTitle sx={{ fontWeight: 800 }}>Update Leave Balances</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {(() => {
              const status = formData.employment_status;
              const caps = getLeaveCaps(status);
              const probation = isProbation(status);
              const totalMax = caps?.total ?? 20;
              const sickMax = caps?.sick ?? 10;
              const casualMax = caps?.casual ?? 10;
              return (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Sick Leaves"
                      name="sick_leaves"
                      type="number"
                      value={leaveBalances.sick_leaves}
                      onChange={handleLeaveChange}
                      disabled={probation}
                      error={Boolean(leaveErrors.sick_leaves)}
                      helperText={
                        leaveErrors.sick_leaves
                          || (probation ? 'Not applicable during probation' : `Maximum ${sickMax} days`)
                      }
                      InputProps={{ inputProps: { min: 0, max: sickMax } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Casual Leaves"
                      name="casual_leaves"
                      type="number"
                      value={leaveBalances.casual_leaves}
                      onChange={handleLeaveChange}
                      disabled={probation}
                      error={Boolean(leaveErrors.casual_leaves)}
                      helperText={
                        leaveErrors.casual_leaves
                          || (probation ? 'Not applicable during probation' : `Maximum ${casualMax} days`)
                      }
                      InputProps={{ inputProps: { min: 0, max: casualMax } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Total Leaves Override"
                      name="total_leaves"
                      type="number"
                      value={leaveBalances.total_leaves}
                      onChange={handleLeaveChange}
                      error={Boolean(leaveErrors.total_leaves)}
                      helperText={leaveErrors.total_leaves || `Maximum ${totalMax} days`}
                      InputProps={{ inputProps: { min: 0, max: totalMax } }}
                    />
                  </Grid>
                </Grid>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setLeaveModalOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' }}>Cancel</Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleLeaveSubmit}
              disabled={Object.values(leaveErrors).some(Boolean)}
              sx={{ ...GRADIENT_BTN, px: 3 }}
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Salary;
